export type TQTooltipPosition = 'top' | 'right' | 'bottom' | 'left';

export interface IQTooltipRect {
  left: number,
  top: number,
  width: number,
  height: number,
}

/** The tooltip's measured size on each side it can be shown on. */
export type TQTooltipSizes = Record<TQTooltipPosition, IQTooltipRect>;

export interface IQTooltipPlacement {
  position: TQTooltipPosition,
  left: number,
  top: number,
  arrowOffset: number,
}

/** Gap between the target and the tooltip, and the minimum distance kept from every viewport edge. */
export const TOOLTIP_GUTTER = 4;

/** Distance the arrow keeps from the tooltip corners once clamping has shifted the box off-centre. */
export const TOOLTIP_ARROW_INSET = 12;

const CANDIDATE_ORDER: Record<TQTooltipPosition, TQTooltipPosition[]> = {
  right: [
    'right',
    'left',
    'top',
    'bottom',
  ],
  left: [
    'left',
    'right',
    'top',
    'bottom',
  ],
  top: [
    'top',
    'bottom',
    'right',
    'left',
  ],
  bottom: [
    'bottom',
    'top',
    'right',
    'left',
  ],
};

export function intersectRect(first: IQTooltipRect, second: IQTooltipRect): IQTooltipRect {
  const left = Math.max(first.left, second.left);
  const top = Math.max(first.top, second.top);
  const right = Math.min(first.left + first.width, second.left + second.width);
  const bottom = Math.min(first.top + first.height, second.top + second.height);
  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

export function rectsOverlap(first: IQTooltipRect, second: IQTooltipRect): boolean {
  const overlap = intersectRect(first, second);
  return overlap.width > 0 && overlap.height > 0;
}

function insetRect(rect: IQTooltipRect, inset: number): IQTooltipRect {
  return {
    left: rect.left + inset,
    top: rect.top + inset,
    width: rect.width - inset * 2,
    height: rect.height - inset * 2,
  };
}

/**
 * Coordinates of a tooltip of `tooltipSize` placed on `position` of `targetRect`,
 * centred on the target along the cross axis. No bounds are considered.
 */
export function placeOnSide(position: TQTooltipPosition, targetRect: IQTooltipRect, tooltipSize: IQTooltipRect): IQTooltipRect {
  const centreLeft = targetRect.left + (targetRect.width - tooltipSize.width) / 2;
  const centreTop = targetRect.top + (targetRect.height - tooltipSize.height) / 2;

  switch (position) {
    case 'right':
      return {
        left: targetRect.left + targetRect.width + TOOLTIP_GUTTER,
        top: centreTop,
        width: tooltipSize.width,
        height: tooltipSize.height,
      };
    case 'left':
      return {
        left: targetRect.left - tooltipSize.width - TOOLTIP_GUTTER,
        top: centreTop,
        width: tooltipSize.width,
        height: tooltipSize.height,
      };
    case 'top':
      return {
        left: centreLeft,
        top: targetRect.top - tooltipSize.height - TOOLTIP_GUTTER,
        width: tooltipSize.width,
        height: tooltipSize.height,
      };
    case 'bottom':
      return {
        left: centreLeft,
        top: targetRect.top + targetRect.height + TOOLTIP_GUTTER,
        width: tooltipSize.width,
        height: tooltipSize.height,
      };
    default: {
      const exhaustiveCheck: never = position;
      throw new Error(`Unhandled tooltip position ${exhaustiveCheck}.`);
    }
  }
}

export function fitsInside(rect: IQTooltipRect, bounds: IQTooltipRect): boolean {
  return rect.left >= bounds.left
    && rect.top >= bounds.top
    && rect.left + rect.width <= bounds.left + bounds.width
    && rect.top + rect.height <= bounds.top + bounds.height;
}

/**
 * Shift `rect` along the cross axis of `position` until it sits inside `bounds`.
 * The main axis is never touched, so a clamped tooltip can never cover its own target.
 */
export function clampToBounds(position: TQTooltipPosition, rect: IQTooltipRect, bounds: IQTooltipRect): IQTooltipRect {
  const isVerticalSide = position === 'top' || position === 'bottom';

  if (isVerticalSide) {
    const maxLeft = bounds.left + bounds.width - rect.width;
    return {
      ...rect,
      left: Math.max(bounds.left, Math.min(rect.left, maxLeft)),
    };
  }

  const maxTop = bounds.top + bounds.height - rect.height;
  return {
    ...rect,
    top: Math.max(bounds.top, Math.min(rect.top, maxTop)),
  };
}

/**
 * Distance from the tooltip's leading cross-axis edge to the centre of the target,
 * so the arrow keeps pointing at the target after the box has been clamped.
 */
export function resolveArrowOffset(position: TQTooltipPosition, targetRect: IQTooltipRect, tooltipRect: IQTooltipRect): number {
  const isVerticalSide = position === 'top' || position === 'bottom';
  const targetCentre = isVerticalSide
    ? targetRect.left + targetRect.width / 2
    : targetRect.top + targetRect.height / 2;
  const tooltipEdge = isVerticalSide ? tooltipRect.left : tooltipRect.top;
  const tooltipExtent = isVerticalSide ? tooltipRect.width : tooltipRect.height;

  const minimumOffset = Math.min(TOOLTIP_ARROW_INSET, tooltipExtent / 2);
  const maximumOffset = Math.max(minimumOffset, tooltipExtent - TOOLTIP_ARROW_INSET);
  return Math.max(minimumOffset, Math.min(targetCentre - tooltipEdge, maximumOffset));
}

function toPlacement(position: TQTooltipPosition, targetRect: IQTooltipRect, tooltipRect: IQTooltipRect): IQTooltipPlacement {
  return {
    position,
    left: tooltipRect.left,
    top: tooltipRect.top,
    arrowOffset: resolveArrowOffset(position, targetRect, tooltipRect),
  };
}

/**
 * Pick the side the tooltip is shown on and the coordinates it is shown at.
 *
 * The cascade prefers keeping the requested side over staying inside the container:
 * 1. preferred side inside the container, 2. preferred side inside the viewport,
 * 3. any other side inside the container, 4. any other side inside the viewport,
 * 5. any side that fits the viewport once shifted along its cross axis,
 * 6. preferred side, shifted as far into the viewport as its cross axis allows.
 *
 * `tooltipSizes` must be the tooltip's real, already-measured size on each side. Measuring
 * a tooltip that is still free to reflow is what makes PrimeVue's own placement collapse
 * text into a one-character column and then decide it fits.
 */
export function resolvePlacement(
  preferredPosition: TQTooltipPosition,
  targetRect: IQTooltipRect,
  tooltipSizes: TQTooltipSizes,
  viewportRect: IQTooltipRect,
  containerRect?: IQTooltipRect,
): IQTooltipPlacement {
  const viewportBounds = insetRect(viewportRect, TOOLTIP_GUTTER);
  const containerBounds = containerRect
    ? intersectRect(containerRect, viewportBounds)
    : viewportBounds;

  const candidates = CANDIDATE_ORDER[preferredPosition];
  const placements = candidates.map((position) => ({
    position,
    rect: placeOnSide(position, targetRect, tooltipSizes[position]),
  }));

  const [
    preferred,
    ...alternatives
  ] = placements;

  const attempts = [
    {
      bounds: containerBounds,
      placements: [
        preferred,
      ],
    },
    {
      bounds: viewportBounds,
      placements: [
        preferred,
      ],
    },
    {
      bounds: containerBounds,
      placements: alternatives,
    },
    {
      bounds: viewportBounds,
      placements: alternatives,
    },
  ];

  for (const attempt of attempts) {
    const match = attempt.placements.find((placement) => fitsInside(placement.rect, attempt.bounds));
    if (match) {
      return toPlacement(match.position, targetRect, match.rect);
    }
  }

  const shifted = placements.map((placement) => ({
    position: placement.position,
    rect: clampToBounds(placement.position, placement.rect, viewportBounds),
  }));
  const shiftedMatch = shifted.find((placement) => fitsInside(placement.rect, viewportBounds));
  if (shiftedMatch) {
    return toPlacement(shiftedMatch.position, targetRect, shiftedMatch.rect);
  }

  return toPlacement(preferred.position, targetRect, shifted[0].rect);
}

/**
 * Coordinates for a side that was already chosen on show. Used while scrolling, where
 * flipping between sides mid-gesture would be far more distracting than a small shift.
 */
export function resolveLockedPlacement(
  position: TQTooltipPosition,
  targetRect: IQTooltipRect,
  tooltipSize: IQTooltipRect,
  viewportRect: IQTooltipRect,
): IQTooltipPlacement {
  const viewportBounds = insetRect(viewportRect, TOOLTIP_GUTTER);
  const clamped = clampToBounds(position, placeOnSide(position, targetRect, tooltipSize), viewportBounds);
  return {
    position,
    left: clamped.left,
    top: clamped.top,
    arrowOffset: resolveArrowOffset(position, targetRect, clamped),
  };
}
